import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '../../lib/api-client';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';

interface SubSellerItem {
  id: string;
  parentId: string;
  name: string;
  payinSharePercent: number;
  payoutSharePercent: number;
  level: number;
  status: string;
}

export function SubSellerList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [credential, setCredential] = useState<{ username: string; tempPassword: string; subSellerName: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['seller', 'sub-sellers'],
    queryFn: () =>
      apiGet<{ items: SubSellerItem[]; total: number }>('/seller/sub-sellers'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiPatch(`/seller/sub-sellers/${id}`, { status: 'inactive' }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'sub-sellers'] });
      } else {
        setError(res.error.message);
      }
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => apiPatch(`/seller/sub-sellers/${id}`, { status: 'active' }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'sub-sellers'] });
      } else {
        setError(res.error.message);
      }
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) =>
      apiPost<{ username: string; tempPassword: string; subSellerName: string }>(`/seller/sub-sellers/${id}/reset-password`),
    onSuccess: (res) => {
      if (res.ok) {
        setCredential(res.data);
      } else {
        setError(res.error.message);
      }
    },
  });

  const subSellers = data?.ok ? data.data.items : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('seller.subSellersTitle')}</h1>
        <Button onClick={() => navigate('/seller/sub-sellers/create')}>
          {t('sellers.addSubSeller')}
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && subSellers.length === 0 && (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          )}
          {subSellers.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('sellers.name')}</TableHead>
                  <TableHead>{t('seller.level')}</TableHead>
                  <TableHead className="text-right">{t('sellers.payinFee')}</TableHead>
                  <TableHead className="text-right">{t('sellers.payoutFee')}</TableHead>
                  <TableHead>{t('sellers.status')}</TableHead>
                  <TableHead>{t('sellers.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subSellers.map((ss) => (
                  <TableRow key={ss.id}>
                    <TableCell>
                      <span style={{ paddingLeft: `${(ss.level - 1) * 16}px` }}>
                        {ss.name}
                      </span>
                    </TableCell>
                    <TableCell>{ss.level}</TableCell>
                    <TableCell className="text-right">{ss.payinSharePercent}%</TableCell>
                    <TableCell className="text-right">{ss.payoutSharePercent}%</TableCell>
                    <TableCell>
                      <Badge variant={ss.status === 'active' ? 'success' : 'danger'}>
                        {t(`common.${ss.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/seller/sub-sellers/${ss.id}/edit`)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(t('sellers.confirmReset', { name: ss.name }))) {
                              resetPasswordMutation.mutate(ss.id);
                            }
                          }}
                          disabled={resetPasswordMutation.isPending}
                        >
                          {t('sellers.resetPassword')}
                        </Button>
                        {ss.status === 'active' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 dark:text-red-400"
                            onClick={() => {
                              if (window.confirm(t('common.confirm'))) {
                                deactivateMutation.mutate(ss.id);
                              }
                            }}
                            disabled={deactivateMutation.isPending}
                          >
                            {t('common.deactivate')}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 dark:text-green-400"
                            onClick={() => activateMutation.mutate(ss.id)}
                            disabled={activateMutation.isPending}
                          >
                            {t('common.activate')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {credential && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('sellers.credentialTitle')}
            </h3>
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              {t('sellers.credentialWarning')}
            </div>
            <div className="mb-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('sellers.name')}</label>
                <div className="font-medium text-gray-900 dark:text-gray-100">{credential.subSellerName}</div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('auth.username')}</label>
                <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm dark:bg-gray-700 dark:text-gray-100">{credential.username}</div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('sellers.tempPassword')}</label>
                <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm font-bold dark:bg-gray-700 dark:text-gray-100">{credential.tempPassword}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={() => {
                  const text = t('sellers.credentialCopyFormat', {
                    name: credential.subSellerName,
                    username: credential.username,
                    password: credential.tempPassword,
                  });
                  navigator.clipboard.writeText(text);
                }}
              >
                {t('sellers.copyCredentials')}
              </Button>
              <Button variant="ghost" onClick={() => setCredential(null)}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
